import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMedal, faTrophy } from "@fortawesome/pro-solid-svg-icons";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Success, SuccessType, TeamListElement, TeamListMinimum, TeamService } from "../../../../client/api";
import { callClientFunc } from "../../../../client";
import { ClavaContext } from "../../../../config/contexts";
import { errorLogging } from "../../../../config/utils";
import { translate, showTranslated } from "../../../../config/translator";
import RoundedRectangle from "../../../components/RoundedRectangle";
import ClavaTextInput from "../../../components/ClavaTextInput";
import loadingGif from "../../../../assets/images/loading.gif";

const noop = (): void => { /* readonly field */ };

const useSuccessFormValidation = (text: string, yearStart: string) => {
  const textInvalid = useRef(false);
  const yearInvalid = useRef(false);
  const errorStateActive = useRef(false);

  const validateForm = useCallback((): boolean => {
    const textValid = text.trim().length >= 2;
    const yearValid = yearStart.trim().length > 0 && /^\d{4}$/.test(yearStart.trim());
    textInvalid.current = !textValid;
    yearInvalid.current = !yearValid;
    return textValid && yearValid;
  }, [text, yearStart]);

  const activateErrors = useCallback(() => {
    errorStateActive.current = true;
  }, []);

  const resetErrors = useCallback(() => {
    errorStateActive.current = false;
  }, []);

  return {
    textInvalid: textInvalid.current && errorStateActive.current,
    yearInvalid: yearInvalid.current && errorStateActive.current,
    validateForm,
    activateErrors,
    resetErrors,
  };
};

type SuccessFormProps = {
  text: string;
  setText: (v: string) => void;
  yearStart: string;
  setYearStart: (v: string) => void;
  successType: SuccessType;
  setSuccessType: (v: SuccessType) => void;
  textInvalid: boolean;
  yearInvalid: boolean;
};

const SuccessForm: React.FC<SuccessFormProps> = ({
  text,
  setText,
  yearStart,
  setYearStart,
  successType,
  setSuccessType,
  textInvalid,
  yearInvalid,
}) => {
  const { l } = useContext(ClavaContext);

  return (
    <>
      <div>
        <div className="mb-1">
          <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
            {translate("leagueCategory", l)}
          </span>
        </div>
        <div className="flex flex-row gap-2">
          <button
            type="button"
            onClick={() => setSuccessType(SuccessType.CHAMPIONSHIP)}
            className={`flex-1 flex flex-row items-center justify-center gap-2 py-2 px-3 rounded-xl border transition-colors ${
              successType === SuccessType.CHAMPIONSHIP
                ? "bg-primary border-primary text-white"
                : "bg-input-bg dark:bg-input-bg-dark border-input-border dark:border-input-border-dark text-font dark:text-font-dark hover:border-primary"
            }`}
          >
            <FontAwesomeIcon icon={faMedal} className="w-4 h-4" />
            <span className="text-sm font-medium">{translate("LEAGUE", l)}</span>
          </button>
          <button
            type="button"
            onClick={() => setSuccessType(SuccessType.CUP_WINNER)}
            className={`flex-1 flex flex-row items-center justify-center gap-2 py-2 px-3 rounded-xl border transition-colors ${
              successType === SuccessType.CUP_WINNER
                ? "bg-primary border-primary text-white"
                : "bg-input-bg dark:bg-input-bg-dark border-input-border dark:border-input-border-dark text-font dark:text-font-dark hover:border-primary"
            }`}
          >
            <FontAwesomeIcon icon={faTrophy} className="w-4 h-4" />
            <span className="text-sm font-medium">{translate("CUP", l)}</span>
          </button>
        </div>
      </div>

      <div>
        <div className="mb-1">
          <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
            {translate("sponsorName", l)}
          </span>
        </div>
        <ClavaTextInput
          onChange={setText}
          error={textInvalid}
          value={text}
          placeholder={translate("sponsorName", l)}
        />
      </div>

      <div>
        <div className="mb-1">
          <span className="text-xs text-font-600 dark:text-font-600-dark font-normal">
            {translate("year", l)}
          </span>
        </div>
        <div className="flex flex-row items-center gap-2">
          <div className="flex-1">
            <ClavaTextInput
              onChange={setYearStart}
              error={yearInvalid}
              value={yearStart}
              placeholder="YYYY"
            />
          </div>
          <span className="text-font dark:text-font-dark font-medium">/</span>
          <div className="flex-1">
            <ClavaTextInput
              onChange={noop}
              value={yearStart ? (parseInt(yearStart, 10) + 1).toString() : ""}
              disabled
              placeholder="YYYY"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const SuccessItem: React.FC<{
  success: Success;
  onUpdate: (successes: Success[]) => void;
  onDelete: (successes: Success[]) => void;
}> = ({ success, onUpdate, onDelete }) => {
  const { l } = useContext(ClavaContext);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);

  const [text, setText] = useState(success.text.textDE ?? "");
  const [yearStart, setYearStart] = useState(success.yearStart?.toString() ?? "");
  const [successType, setSuccessType] = useState<SuccessType>(success.successType);

  const { textInvalid, yearInvalid, validateForm, activateErrors, resetErrors } = useSuccessFormValidation(text, yearStart);

  const handleSave = useCallback(() => {
    activateErrors();
    if (!validateForm() || saving) {
      return;
    }

    setSaving(true);
    const yearStartNum = parseInt(yearStart, 10);
    const yearEndNum = yearStartNum + 1;

    const patchData: any = {};
    if (text !== success.text.textDE) {
      patchData.text = { textDE: text, textIT: text, textEN: text };
    }
    if (yearStartNum !== success.yearStart) {
      patchData.yearStart = yearStartNum;
      patchData.yearEnd = yearEndNum;
    }
    if (successType !== success.successType) {
      patchData.successType = successType;
    }

    if (Object.keys(patchData).length === 0) {
      setSaving(false);
      setEditing(false);
      return;
    }

    callClientFunc(
      TeamService.patchTeamSuccessTeamSuccessSuccessIdPatch,
      false,
      success.id,
      patchData
    ).then((res) => {
      onUpdate(res);
      setEditing(false);
      setSaving(false);
    }, (error) => {
      errorLogging(error);
      setSaving(false);
    });
  }, [text, yearStart, successType, success, validateForm, saving, onUpdate, activateErrors]);

  const handleCancel = useCallback(() => {
    setText(success.text.textDE ?? "");
    setYearStart(success.yearStart?.toString() ?? "");
    setSuccessType(success.successType);
    resetErrors();
    setEditing(false);
  }, [success, resetErrors]);

  const handleDelete = useCallback(() => {
    setDeleting(true);
    callClientFunc(
      TeamService.deleteTeamSuccessTeamSuccessSuccessIdDelete,
      false,
      success.id
    ).then((res) => {
      onDelete(res);
      setDeleting(false);
    }, (error) => {
      errorLogging(error);
      setDeleting(false);
    });
  }, [success.id, onDelete]);

  const formatYear = useCallback(() => {
    if (success.yearEnd && success.yearEnd > 0) {
      return `${success.yearStart}/${success.yearEnd}`;
    }
    return success.yearStart.toString();
  }, [success]);

  return (
    <div className="p-3 rounded-xl bg-bg dark:bg-bg-dark border border-light-gray dark:border-light-gray-dark relative">
      {!editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="absolute top-3 right-3 text-primary hover:text-btn-active transition-colors text-sm font-medium"
        >
          {translate("edit", l)}
        </button>
      )}

      {editing ? (
        <div className="flex flex-col gap-4">
          <SuccessForm
            text={text}
            setText={setText}
            yearStart={yearStart}
            setYearStart={setYearStart}
            successType={successType}
            setSuccessType={setSuccessType}
            textInvalid={textInvalid}
            yearInvalid={yearInvalid}
          />

          <div className="flex flex-row gap-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="flex-1 rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-red active:bg-red hover:bg-red text-white text-sm font-semibold disabled:opacity-60"
            >
              {deleting ? (
                <img src={loadingGif} alt="Loading" className="w-4 h-4" />
              ) : (
                translate("deleteSponsor", l)
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="flex-1 rounded-full py-2 px-4 flex flex-row items-center justify-center border border-light-gray dark:border-light-gray-dark bg-bg-secondary dark:bg-bg-secondary-dark text-font dark:text-font-dark text-sm font-semibold disabled:opacity-60"
            >
              {translate("cancel", l)}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-primary active:bg-btn-active hover:bg-btn-active text-font-dark text-sm font-semibold disabled:opacity-60"
            >
              {saving ? (
                <img src={loadingGif} alt="Loading" className="w-4 h-4" />
              ) : (
                translate("save", l)
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-row items-center gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-10 dark:bg-primary-10-dark flex items-center justify-center">
            <FontAwesomeIcon
              icon={success.successType === SuccessType.CHAMPIONSHIP ? faMedal : faTrophy}
              className="w-5 h-5 text-primary"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-font dark:text-font-dark truncate">
              {showTranslated(success.text, l)}
            </div>
            <div className="text-xs text-font-600 dark:text-font-600-dark">
              {formatYear()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NewSuccessForm: React.FC<{
  teamId: number;
  onCreated: (successes: Success[]) => void;
  onCancel: () => void;
}> = ({ teamId, onCreated, onCancel }) => {
  const { l } = useContext(ClavaContext);
  const [saving, setSaving] = useState(false);

  const [text, setText] = useState("");
  const [yearStart, setYearStart] = useState("");
  const [successType, setSuccessType] = useState<SuccessType>(SuccessType.CHAMPIONSHIP);

  const { textInvalid, yearInvalid, validateForm, activateErrors, resetErrors } = useSuccessFormValidation(text, yearStart);

  const handleSave = useCallback(() => {
    activateErrors();
    if (!validateForm() || saving) {
      return;
    }

    setSaving(true);
    const yearStartNum = parseInt(yearStart, 10);
    const yearEndNum = yearStartNum + 1;

    callClientFunc(
      TeamService.createTeamSuccessTeamSuccessTeamIdPost,
      false,
      teamId,
      {
        text: { textDE: text, textIT: text, textEN: text },
        yearStart: yearStartNum,
        yearEnd: yearEndNum,
        successType,
      }
    ).then((res) => {
      onCreated(res);
      setSaving(false);
    }, (error) => {
      errorLogging(error);
      setSaving(false);
    });
  }, [text, yearStart, successType, teamId, validateForm, saving, onCreated, activateErrors]);

  const handleCancel = useCallback(() => {
    resetErrors();
    onCancel();
  }, [onCancel, resetErrors]);

  return (
    <div className="p-4 rounded-xl bg-bg dark:bg-bg-dark border-2 border-dashed border-primary">
      <div className="flex flex-col gap-4">
        <SuccessForm
          text={text}
          setText={setText}
          yearStart={yearStart}
          setYearStart={setYearStart}
          successType={successType}
          setSuccessType={setSuccessType}
          textInvalid={textInvalid}
          yearInvalid={yearInvalid}
        />

        <div className="flex flex-row gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="flex-1 rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-red active:bg-red hover:bg-red text-white text-sm font-semibold disabled:opacity-60"
          >
            {translate("cancel", l)}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-primary active:bg-btn-active hover:bg-btn-active text-font-dark text-sm font-semibold disabled:opacity-60"
          >
            {saving ? (
              <img src={loadingGif} alt="Loading" className="w-4 h-4" />
            ) : (
              translate("save", l)
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const SuccessesManagement: React.FC<{ team: TeamListMinimum | TeamListElement }> = ({ team }) => {
  const { l } = useContext(ClavaContext);
  const [successes, setSuccesses] = useState<Success[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setLoading(true);
    callClientFunc(
      TeamService.getTeamSuccessesTeamSuccessTeamIdGet,
      false,
      team.id
    ).then((successList) => {
      setSuccesses(successList || []);
      setLoading(false);
    }, (error) => {
      errorLogging(error);
      setLoading(false);
    });
  }, [team.id]);

  const handleUpdate = useCallback((newSuccesses: Success[]) => {
    setSuccesses(newSuccesses);
  }, []);

  const handleDelete = useCallback((newSuccesses: Success[]) => {
    setSuccesses(newSuccesses);
  }, []);

  const handleCreated = useCallback((newSuccesses: Success[]) => {
    setSuccesses(newSuccesses);
    setAdding(false);
  }, []);

  if (loading) {
    return (
      <RoundedRectangle
        className="w-full bg-bg-secondary dark:bg-bg-secondary-dark relative mt-6"
        hasBorder={false}
        hasTitle
        titleKey="teamtitle"
      >
        <div className="text-center py-8 text-font-600 dark:text-font-600-dark">
          {translate("loading", l)}
        </div>
      </RoundedRectangle>
    );
  }

  return (
    <RoundedRectangle
      className="w-full bg-bg-secondary dark:bg-bg-secondary-dark relative mt-6"
      hasBorder={false}
      hasTitle
      titleKey="teamtitle"
    >
      <div className="px-6 py-4">
        <div className="flex flex-col gap-3">
          {successes.map((success) => (
            <SuccessItem
              key={success.id}
              success={success}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}

          {adding ? (
            <NewSuccessForm
              teamId={team.id}
              onCreated={handleCreated}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex flex-row items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-primary dark:border-primary bg-primary-50 dark:bg-primary-50-dark hover:bg-primary-10 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-white dark:bg-white flex items-center justify-center">
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm font-medium text-font-600 dark:text-font-600-dark">
                {translate("addSponsor", l)}
              </span>
            </button>
          )}
        </div>
      </div>
    </RoundedRectangle>
  );
};

export default SuccessesManagement;
